import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBrandConfig } from "@/hooks/use-brand-config";
import { mergeEntityRows } from "@/lib/entity-merge";
import { useEntitySuppressions } from "@/hooks/use-entity-suppressions";

interface ContactDetails {
  email: string;
  phone: string;
}

interface LoanAmountConfig {
  min_amount: number;
  max_amount: number;
  step: number;
  default_amount: number;
  isLoaded?: boolean;
}

const defaultContact: ContactDetails = {
  email: "",
  phone: "",
};

const defaultLoanConfigs: Record<string, LoanAmountConfig> = {
  apply: { min_amount: 2000, max_amount: 45000, step: 500, default_amount: 25000 },
  explore: { min_amount: 1000, max_amount: 100000, step: 500, default_amount: 15000 },
  hero_slider: { min_amount: 2000, max_amount: 45000, step: 500, default_amount: 25000 },
};

export function useSiteContact() {
  const [contact, setContact] = useState<ContactDetails>(defaultContact);
  const brand = useBrandConfig();
  const suppressedIds = useEntitySuppressions("site_contact_details");

  useEffect(() => {
    if (!brand.isLoaded) return;

    supabase
      .from("site_contact_details")
      .select("id, key, value, company_entity_id")
      .eq("is_active", true)
      .then(({ data }) => {
        if (!data || data.length === 0) {
          setContact(defaultContact);
          return;
        }

        // Merge per-key: entity overrides replace globals with same key
        const merged = mergeEntityRows(
          data as any[],
          brand.entityId,
          (r: any) => r.key,
          suppressedIds
        );

        const map: Record<string, string> = {};
        merged.forEach((row: any) => {
          map[row.key] = row.value;
        });
        setContact({
          email: map.email || defaultContact.email,
          phone: map.phone || defaultContact.phone,
        });
      });
  }, [brand.isLoaded, brand.entityId, suppressedIds]);

  return contact;
}

export function useLoanAmountConfig(context: string) {
  const fallback = defaultLoanConfigs[context] || defaultLoanConfigs.apply;
  const [config, setConfig] = useState<LoanAmountConfig>({ ...fallback, isLoaded: false });
  const brand = useBrandConfig();
  const suppressedIds = useEntitySuppressions("loan_amount_config");

  useEffect(() => {
    if (!brand.isLoaded) return;

    const currentFallback = defaultLoanConfigs[context] || defaultLoanConfigs.apply;
    setConfig({ ...currentFallback, isLoaded: false });

    supabase
      .from("loan_amount_config")
      .select("id, min_amount, max_amount, step, default_amount, company_entity_id")
      .eq("context", context)
      .eq("is_active", true)
      .then(({ data }) => {
        if (data && data.length > 0) {
          // Merge entity overrides per context
          const merged = mergeEntityRows(
            data as any[],
            brand.entityId,
            (r: any) => r.context || context,
            suppressedIds
          );
          if (merged.length > 0) {
            const row = merged[0] as any;
            setConfig({
              min_amount: row.min_amount,
              max_amount: row.max_amount,
              step: row.step,
              default_amount: row.default_amount ?? currentFallback.default_amount,
              isLoaded: true,
            });
            return;
          }
        }
        setConfig({ ...currentFallback, isLoaded: true });
      });
  }, [context, brand.isLoaded, brand.entityId, suppressedIds]);

  return config;
}
